import React, { useState, useEffect, useContext } from "react";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
    Switch,
    makeStyles,
    TextField,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";
import { Formik, Form, Field } from "formik";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as PlayArrowIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import useHelps from "../../hooks/useHelps";
import { i18n } from "../../translate/i18n";

// Layout fiel ao mockup "Configurações · Ajuda" (docs/design/aeros-dark,
// bloco <!-- AJUDA -->): cabeçalho + botão gradiente "Novo conteúdo" e grid
// 2 colunas de cards com topo 96px (gradiente/thumbnail do YouTube), corpo
// 12×14, chip "YouTube · código" e icon buttons 26px. O formulário de
// cadastro (HelpManagerForm) vive dentro de um Dialog do hook compartilhado.
const useStyles = makeStyles((theme) => {
    const isLight = theme.palette.type === "light";
    return {
        root: {
            width: "100%",
        },
        cardsGrid: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 10,
            [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
        },
        helpCard: {
            backgroundColor: theme.palette.aeros.surface,
            border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.07)"}`,
            borderRadius: 14,
            overflow: "hidden",
            minWidth: 0,
            transition: "border-color 0.15s ease",
            "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.4),
            },
        },
        helpCardTop: {
            height: 96,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage:
                "linear-gradient(135deg, rgba(160,68,255,0.25), rgba(34,211,238,0.15))",
            backgroundSize: "cover",
            backgroundPosition: "center",
        },
        playCircle: {
            width: 36,
            height: 36,
            borderRadius: "50%",
            // Círculo escuro fixo do mockup (rgba(7,8,13,0.7)) — o ícone claro
            // sobre ele é legível nos dois temas.
            backgroundColor: "rgba(7,8,13,0.7)",
            color: "#F1F2F6",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            "& .MuiSvgIcon-root": { fontSize: 20 },
        },
        helpCardBody: {
            padding: "12px 14px",
        },
        helpCardDesc: {
            marginTop: 3,
            fontSize: 11.5,
            lineHeight: 1.5,
            color: theme.palette.text.secondary,
        },
        helpCardFooter: {
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 10,
            minWidth: 0,
        },
        helpCardActions: {
            marginLeft: "auto",
            display: "flex",
            gap: 4,
            flexShrink: 0,
        },
        formField: {
            marginBottom: 12,
            "&:last-child": { marginBottom: 0 },
        },
        dangerText: {
            color: theme.palette.error.main,
        },
    };
});

const EMPTY_RECORD = {
    title: "",
    description: "",
    video: "",
    category: "",
    featured: false,
    duration: "",
};

// "12:40" ↔ segundos (campo Duração do formulário)
export const durationToSeconds = (v) => {
    if (v == null || v === "") return null;
    const m = String(v).trim().match(/^(\d+):([0-5]?\d)$/);
    if (m) return Number(m[1]) * 60 + Number(m[2]);
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
};
export const secondsToDuration = (s) => (s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : "");

export function HelpManagerForm(props) {
    const { onSubmit, onDelete, onCancel, initialValue, loading, open } = props;
    const classes = useStyles();
    const aeros = useAerosSettingsStyles();

    const [record, setRecord] = useState(initialValue);

    useEffect(() => {
        setRecord({ ...EMPTY_RECORD, ...initialValue, duration: secondsToDuration(initialValue?.duration) });
    }, [initialValue]);

    const handleSubmit = async (data) => {
        onSubmit({ ...data, duration: durationToSeconds(data.duration), category: (data.category || "").trim() });
    };

    const isEditing = record.id !== undefined;

    return (
        <Dialog
            open={!!open}
            onClose={() => onCancel()}
            fullWidth
            maxWidth="sm"
            classes={{ paper: aeros.dialogPaper }}
        >
            <Formik
                enableReinitialize
                initialValues={record}
                onSubmit={(values, { resetForm }) =>
                    setTimeout(() => {
                        handleSubmit(values);
                        resetForm();
                    }, 500)
                }
            >
                {() => (
                    <Form>
                        <DialogTitle disableTypography className={aeros.dialogTitle}>
                            {isEditing ? "Editar conteúdo" : "Novo conteúdo"}
                        </DialogTitle>
                        <DialogContent className={aeros.dialogContent}>
                            <div className={classes.formField}>
                                <label className={aeros.fieldLabel} htmlFor="help-title">
                                    Título
                                </label>
                                <Field
                                    as={TextField}
                                    id="help-title"
                                    name="title"
                                    variant="outlined"
                                    className={aeros.input}
                                    placeholder="Ex.: Primeiros passos no Pulse"
                                />
                            </div>
                            <div className={classes.formField}>
                                <label className={aeros.fieldLabel} htmlFor="help-video">
                                    {i18n.t("helps.settings.codeVideo")}
                                </label>
                                <Field
                                    as={TextField}
                                    id="help-video"
                                    name="video"
                                    variant="outlined"
                                    className={aeros.input}
                                    placeholder="Código do vídeo no YouTube"
                                />
                            </div>
                            <div className={classes.formField} style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10 }}>
                                <div>
                                    <label className={aeros.fieldLabel} htmlFor="help-category">{i18n.t("aeros.helpsManager.category")}</label>
                                    <Field as={TextField} id="help-category" name="category" variant="outlined" className={aeros.input} placeholder={i18n.t("aeros.helpsManager.categoryPlaceholder")} />
                                </div>
                                <div>
                                    <label className={aeros.fieldLabel} htmlFor="help-duration">{i18n.t("aeros.helpsManager.duration")}</label>
                                    <Field as={TextField} id="help-duration" name="duration" variant="outlined" className={aeros.input} placeholder="mm:ss" />
                                </div>
                            </div>
                            <div className={classes.formField}>
                                <label className={aeros.fieldLabel} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                    <Field name="featured">
                                        {({ field, form }) => (
                                            <Switch className={aeros.toggleSwitchSmall} checked={Boolean(field.value)} onChange={(e) => form.setFieldValue("featured", e.target.checked)} />
                                        )}
                                    </Field>
                                    {i18n.t("aeros.helpsManager.featured")}
                                </label>
                            </div>
                            <div className={classes.formField}>
                                <label className={aeros.fieldLabel} htmlFor="help-description">
                                    {i18n.t("helps.settings.description")}
                                </label>
                                <Field
                                    as={TextField}
                                    id="help-description"
                                    name="description"
                                    variant="outlined"
                                    className={aeros.input}
                                    multiline
                                    minRows={3}
                                />
                            </div>
                        </DialogContent>
                        <DialogActions className={aeros.dialogActions}>
                            {isEditing ? (
                                <Button
                                    variant="outlined"
                                    className={`${aeros.ghostButton} ${classes.dangerText}`}
                                    disabled={loading}
                                    onClick={() => onDelete(record)}
                                >
                                    {i18n.t("helps.settings.delete")}
                                </Button>
                            ) : null}
                            <Button
                                variant="outlined"
                                className={aeros.ghostButton}
                                disabled={loading}
                                onClick={() => onCancel()}
                            >
                                Cancelar
                            </Button>
                            <ButtonWithSpinner
                                loading={loading}
                                type="submit"
                                variant="contained"
                                color="primary"
                                className={aeros.gradientButtonSmall}
                            >
                                {i18n.t("helps.settings.save")}
                            </ButtonWithSpinner>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}

export function HelpsManagerGrid(props) {
    const { records, onSelect, onDelete } = props;
    const classes = useStyles();
    const aeros = useAerosSettingsStyles();

    if (!records.length) {
        return (
            <div className={aeros.card}>
                <div className={aeros.emptyRow}>Nenhum conteúdo cadastrado ainda.</div>
            </div>
        );
    }

    return (
        <div className={classes.cardsGrid}>
            {records.map((row) => {
                const thumbnail = row.video
                    ? `https://img.youtube.com/vi/${row.video}/mqdefault.jpg`
                    : null;
                return (
                    <div key={row.id} className={classes.helpCard}>
                        <div
                            className={classes.helpCardTop}
                            style={
                                thumbnail
                                    ? {
                                          backgroundImage: `linear-gradient(135deg, rgba(160,68,255,0.25), rgba(34,211,238,0.15)), url(${thumbnail})`,
                                      }
                                    : undefined
                            }
                        >
                            <div className={classes.playCircle}>
                                <PlayArrowIcon />
                            </div>
                        </div>
                        <div className={classes.helpCardBody}>
                            <div className={aeros.cardTitle}>{row.title || "-"}</div>
                            <div className={classes.helpCardDesc}>
                                {row.description || "-"}
                            </div>
                            <div className={classes.helpCardFooter}>
                                {row.video ? (
                                    <span className={aeros.chipNeutral}>
                                        YouTube · {row.video}
                                    </span>
                                ) : null}
                                <div className={classes.helpCardActions}>
                                    <IconButton
                                        className={aeros.iconButton}
                                        onClick={() => onSelect(row)}
                                        aria-label="editar"
                                        title="Editar"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        className={`${aeros.iconButton} ${aeros.iconButtonDanger}`}
                                        onClick={() => onDelete(row)}
                                        aria-label="excluir"
                                        title={i18n.t("helps.settings.delete")}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// URLs exibidas na tela de Ajuda ("Falar com suporte", "Central de
// documentação", "Abrir chamado"). Salvas em CompaniesSettings via
// PUT /companySettings { column, data }.
const LINK_FIELDS = [
    ["helpSupportUrl", "linkSupport", "https://wa.me/55…"],
    ["helpDocsUrl", "linkDocs", "https://docs.suaempresa.com.br"],
    ["helpTicketUrl", "linkTicket", "https://suporte.suaempresa.com.br"],
];

function HelpLinksCard({ aeros }) {
    const { getAll, update } = useCompanySettings();
    const { user } = useContext(AuthContext);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState("");

    useEffect(() => {
        if (!user?.companyId) return;
        getAll(user.companyId).then((data) => {
            const s = Array.isArray(data) ? data[0] : data;
            setValues(Object.fromEntries(LINK_FIELDS.map(([k]) => [k, s?.[k] || ""])));
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.companyId]);

    const save = async (column) => {
        setSaving(column);
        try {
            await update({ column, data: values[column] || null });
            toast.success(i18n.t("aeros.helpsManager.linkSaved"));
        } catch (err) {
            toast.error(i18n.t("aeros.helpsManager.linkError"));
        } finally {
            setSaving("");
        }
    };

    return (
        <div className={aeros.card} style={{ marginBottom: 16 }}>
            <div className={aeros.cardTitle}>{i18n.t("aeros.helpsManager.linksTitle")}</div>
            <div className={aeros.sectionSubtitle} style={{ marginBottom: 12 }}>{i18n.t("aeros.helpsManager.linksSubtitle")}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
                {LINK_FIELDS.map(([key, labelKey, placeholder]) => (
                    <div key={key}>
                        <label className={aeros.fieldLabel} htmlFor={`link-${key}`}>{i18n.t(`aeros.helpsManager.${labelKey}`)}</label>
                        <TextField id={`link-${key}`} variant="outlined" className={aeros.input} placeholder={placeholder} value={values[key] || ""} onChange={(e) => { const valor = e.target.value; setValues((v) => ({ ...v, [key]: valor })); }} onBlur={() => save(key)} disabled={saving === key} fullWidth />
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function HelpsManager() {
    const classes = useStyles();
    const aeros = useAerosSettingsStyles();
    const { list, save, update, remove } = useHelps();

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState([]);
    const [record, setRecord] = useState(EMPTY_RECORD);

    useEffect(() => {
        async function fetchData() {
            await loadHelps();
        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadHelps = async () => {
        setLoading(true);
        try {
            const helpList = await list();
            setRecords(helpList);
        } catch (e) {
            toast.error("Não foi possível carregar a lista de registros");
        }
        setLoading(false);
    };

    const handleSubmit = async (data) => {
        setLoading(true);
        try {
            if (data.id !== undefined) {
                await update(data);
            } else {
                await save(data);
            }
            await loadHelps();
            handleCancel();
            toast.success("Operação realizada com sucesso!");
        } catch (e) {
            toast.error(
                "Não foi possível realizar a operação. Verifique se já existe uma helpo com o mesmo nome ou se os campos foram preenchidos corretamente"
            );
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await remove(record.id);
            await loadHelps();
            handleCancel();
            toast.success("Operação realizada com sucesso!");
        } catch (e) {
            toast.error("Não foi possível realizar a operação");
        }
        setLoading(false);
    };

    const handleOpenDeleteDialog = (data) => {
        if (data && data.id !== undefined) {
            handleSelect(data);
        }
        setShowConfirmDialog(true);
    };

    const handleCancel = () => {
        setRecord(EMPTY_RECORD);
        setFormOpen(false);
    };

    const handleSelect = (data) => {
        setRecord({
            id: data.id,
            title: data.title || "",
            description: data.description || "",
            video: data.video || "",
        });
    };

    const handleEdit = (data) => {
        handleSelect(data);
        setFormOpen(true);
    };

    const handleNew = () => {
        setRecord(EMPTY_RECORD);
        setFormOpen(true);
    };

    return (
        <div className={classes.root}>
            <div className={aeros.sectionHead}>
                <div>
                    <div className={aeros.sectionTitle}>Central de ajuda</div>
                    <div className={aeros.sectionSubtitle}>
                        Vídeos e artigos exibidos aos usuários na página Ajuda
                    </div>
                </div>
                <div className={aeros.sectionActions}>
                    <Button
                        variant="contained"
                        color="primary"
                        className={aeros.gradientButton}
                        startIcon={<AddIcon />}
                        onClick={handleNew}
                    >
                        Novo conteúdo
                    </Button>
                </div>
            </div>

            <HelpLinksCard aeros={aeros} />

            <HelpsManagerGrid
                records={records}
                onSelect={handleEdit}
                onDelete={handleOpenDeleteDialog}
            />

            <HelpManagerForm
                open={formOpen}
                initialValue={record}
                onDelete={handleOpenDeleteDialog}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                loading={loading}
            />

            <ConfirmationModal
                title="Exclusão de Registro"
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => handleDelete()}
            >
                Deseja realmente excluir esse registro?
            </ConfirmationModal>
        </div>
    );
}
